import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, FileX2, LucideIcon } from "lucide-react";

type StateTone = "default" | "warning" | "danger";

interface RegistrationStateCardProps {
  title: string;
  description: string;
  note?: string;
  icon?: "calendar" | "clock" | "invalid";
  tone?: StateTone;
}

const iconMap: Record<NonNullable<RegistrationStateCardProps["icon"]>, LucideIcon> = {
  calendar: Calendar,
  clock: Clock,
  invalid: FileX2,
};

const toneClasses: Record<StateTone, { icon: string; title: string }> = {
  default: {
    icon: "text-blue-500",
    title: "text-blue-600",
  },
  warning: {
    icon: "text-orange-500",
    title: "text-orange-600",
  },
  danger: {
    icon: "text-red-500",
    title: "text-red-600",
  },
};

export function RegistrationStateCard({
  title,
  description,
  note,
  icon = "calendar",
  tone = "default",
}: RegistrationStateCardProps) {
  const navigate = useNavigate();
  const Icon = iconMap[icon];
  const colors = toneClasses[tone];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            <Icon className={`h-12 w-12 mx-auto ${colors.icon}`} />
            <div className="space-y-2">
              <h3 className={`text-lg font-semibold ${colors.title}`}>{title}</h3>
              <p className="text-gray-600">{description}</p>
              {note ? <p className="text-sm text-gray-500">{note}</p> : null}
            </div>
            <Button onClick={() => navigate("/")}>Back to Home</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
